import {LEAGUES} from "../constants/leagues";
import {PLAYER_BONUS_TYPES} from "./playerBonus";
import {getLeagueByRating} from "../utils/rating/getLeagueByRating";
import {getLeagueBaseRating} from "../utils/rating/getLeagueBaseRating";
import {getNextLeagueMinRating} from "../utils/rating/shiftLeague";
import {getLeagueConfig} from "./leagues";

/**
 * «Явные» боты рейтинговых матчей — второй гандикап против сильных живых
 * игроков, в отличие от скрытого давления (см. botPressure.js).
 *
 * Ботов, которых сервер набрал в лобби из очереди, на старте матча с некоторой
 * вероятностью подменяют на явных: они честно помечены ботами для всех игроков
 * и получают экономический бонус — тот же самый, что можно выдать боту в
 * локальной игре (playerBonus.js). Взамен они не участвуют в наградах и в
 * рейтинге.
 *
 * Бросок делается на каждого бота отдельно, поэтому состав бывает смешанным —
 * и это к лучшему: рядом с помеченным ботом непомеченный участник читается как
 * живой игрок.
 *
 * И вероятность подмены, и лига самого бота определяются САМЫМ СЛАБЫМ живым
 * игроком лобби: они не должны зависеть от того, кто там сильнее всех.
 * Вероятность — по его ЛИГЕ (VISIBLE_RANKED_BOT_LEAGUE_RULES), лига бота — по
 * его РЕЙТИНГУ (VISIBLE_RANKED_BOT_LEAGUE_DISTRIBUTION): внутри одной лиги
 * новичок и тот, кто вот-вот из неё выйдет, получают разных соперников.
 *
 * Отдельный случай — витринные матчи ботов друг с другом, где живых игроков
 * нет: там явные боты нужны не как гандикап, а чтобы матч выглядел как у
 * живых игроков (см. BOTS_ONLY_MATCH_VISIBLE_BOT_LEAGUE_CHANCES ниже).
 */

// Пауза между действиями явного бота. Случайный разброс и личный множитель
// (см. utils/bots/online/botPacing) нужны обычным ботам, чтобы их не вычисляли
// по ровному ритму. Явный бот и так помечен ботом для всех, притворяться ему
// не перед кем — значит и заставлять игроков ждать незачем: ровная секунда
export const VISIBLE_RANKED_BOT_ACTION_DELAY_MS = 1000;

// Пауза между действиями явного бота, когда его ходы ведет САМ СЕРВЕР: у матча
// нет подключенного исполнителя — ни живого игрока, ни зрителя с аккаунтом
// (см. runServerBotFallbackStep на сервере). Секунда выше нужна, чтобы не
// заставлять игроков ждать; здесь ждать некому, и спешить незачем. Так бывает
// прежде всего в витринных матчах ботов, пока их никто не смотрит: с секундной
// паузой матчи с явными ботами заканчивались бы заметно раньше остальных.
// Вернувшийся игрок этой паузы не ждет: работу ему сервер отдает сразу при
// подключении (см. decideBotWorkOnMemberReconnect)
export const VISIBLE_RANKED_BOT_SERVER_ACTION_DELAY_MS = 5000;

// Насколько выше нижней границы своей лиги «стоит» явный бот в расчетах.
// Своего рейтинга в зачете у него нет, а одной лиги мало: награды за рейтинговые
// скины считаются от СРЕДНЕГО рейтинга соперников, а среднему нужно число
export const VISIBLE_RANKED_BOT_RATING_OFFSET = 500;

// Вероятность подмены — по лиге самого слабого живого игрока. Бонус — по лиге
// САМОГО БОТА (она может отличаться, см. resolveVisibleBotLeague), поэтому
// таблица служит обеим ролям, но читается по разным лигам.
// У бронзы бонуса нет вовсе: там подмена нужна ради честной пометки, а не ради
// гандикапа. Божественный бот получает «божество»: экономика чуть богаче рубина
// плюс очки к доходу за каждый город и кость урона, которая всегда дает 0 (см.
// configs/playerBonus)
export const VISIBLE_RANKED_BOT_LEAGUE_RULES = {
  [LEAGUES.BRONZE]: {replaceChance: 0.4, bonusType: PLAYER_BONUS_TYPES.NONE},
  [LEAGUES.SILVER]: {replaceChance: 0.4, bonusType: PLAYER_BONUS_TYPES.MEDIUM},
  [LEAGUES.GOLD]: {replaceChance: 0.75, bonusType: PLAYER_BONUS_TYPES.BIG},
  [LEAGUES.DIAMOND]: {replaceChance: 0.9, bonusType: PLAYER_BONUS_TYPES.LARGE},
  [LEAGUES.RUBY]: {replaceChance: 1, bonusType: PLAYER_BONUS_TYPES.GREAT},
  [LEAGUES.GOD]: {replaceChance: 1, bonusType: PLAYER_BONUS_TYPES.DIVINE},
};

// Лига подменного бота — по РЕЙТИНГУ самого слабого живого игрока лобби, а не
// по его лиге: шаг таблицы мельче лиги, поэтому соперник растёт вместе с
// игроком внутри лиги, а не скачком на её границе. Действует один порог — самый
// высокий из тех, что игрок перешагнул (до 500 — всегда бронза).
//
// Смесь лиг в каждой строке нужна, чтобы состав не выглядел подстроенным под
// игрока: у большинства порогов бот либо ровня, либо ступенью выше или ниже.
// Доли внутри строки суммируются в 1 (проверяется тестом).
export const VISIBLE_RANKED_BOT_LEAGUE_DISTRIBUTION = [
  {minRating: 0, leagues: [[LEAGUES.BRONZE, 1]]},
  {minRating: 500, leagues: [[LEAGUES.BRONZE, 0.5], [LEAGUES.SILVER, 0.5]]},
  {minRating: 800, leagues: [[LEAGUES.BRONZE, 0.15], [LEAGUES.SILVER, 0.7], [LEAGUES.GOLD, 0.15]]},
  {minRating: 1200, leagues: [[LEAGUES.SILVER, 0.7], [LEAGUES.GOLD, 0.3]]},
  {minRating: 1500, leagues: [[LEAGUES.SILVER, 0.5], [LEAGUES.GOLD, 0.5]]},
  {minRating: 2000, leagues: [[LEAGUES.SILVER, 0.15], [LEAGUES.GOLD, 0.7], [LEAGUES.DIAMOND, 0.15]]},
  {minRating: 2500, leagues: [[LEAGUES.GOLD, 0.6], [LEAGUES.DIAMOND, 0.4]]},
  // Рубина в начале алмазной лиги нет: первая встреча с ним — с 3500
  {minRating: 3000, leagues: [[LEAGUES.GOLD, 0.2], [LEAGUES.DIAMOND, 0.8]]},
  {minRating: 3500, leagues: [[LEAGUES.DIAMOND, 0.8], [LEAGUES.RUBY, 0.2]]},
  {minRating: 4000, leagues: [[LEAGUES.DIAMOND, 0.7], [LEAGUES.RUBY, 0.3]]},
  {minRating: 4500, leagues: [[LEAGUES.DIAMOND, 0.5], [LEAGUES.RUBY, 0.5]]},
  {minRating: 5000, leagues: [[LEAGUES.RUBY, 1]]},
  // Божественные боты появляются задолго до самой лиги (10000): с 7000 их доля
  // растет, а с 10000 других соперников-ботов не остается
  {minRating: 7000, leagues: [[LEAGUES.RUBY, 0.8], [LEAGUES.GOD, 0.2]]},
  {minRating: 7500, leagues: [[LEAGUES.RUBY, 0.7], [LEAGUES.GOD, 0.3]]},
  {minRating: 8000, leagues: [[LEAGUES.RUBY, 0.6], [LEAGUES.GOD, 0.4]]},
  {minRating: 8500, leagues: [[LEAGUES.RUBY, 0.5], [LEAGUES.GOD, 0.5]]},
  {minRating: 9000, leagues: [[LEAGUES.RUBY, 0.3], [LEAGUES.GOD, 0.7]]},
  {minRating: 9500, leagues: [[LEAGUES.RUBY, 0.1], [LEAGUES.GOD, 0.9]]},
  {minRating: 10000, leagues: [[LEAGUES.GOD, 1]]},
];

/**
 * Рейтинг, которым явный бот участвует в расчетах: и в среднем рейтинге
 * соперников (награды), и в поправке на разницу лиг (изменение рейтинга живых).
 * Ровно одно число на оба расчета — иначе они разъехались бы между собой.
 *
 * Отступ подрезается по верхней границе лиги: у бронзы ширина всего 500, и без
 * подрезки бот из нее попадал бы в серебро, то есть считался бы сильнее, чем
 * выдан.
 */
export const getVisibleRankedBotRating = (leagueId) => {
  const withOffset = getLeagueBaseRating(leagueId) + VISIBLE_RANKED_BOT_RATING_OFFSET;
  const nextLeagueMinRating = getNextLeagueMinRating(leagueId);

  return nextLeagueMinRating === undefined
    ? withOffset
    : Math.min(withOffset, nextLeagueMinRating - 1);
};

/** Строка таблицы распределения для рейтинга: самый высокий перешагнутый порог */
export const getVisibleRankedBotLeagueRow = (weakestRating) => {
  const rating = Math.max(0, weakestRating || 0);
  let row = VISIBLE_RANKED_BOT_LEAGUE_DISTRIBUTION[0];
  for (const candidate of VISIBLE_RANKED_BOT_LEAGUE_DISTRIBUTION) {
    if (rating >= candidate.minRating) {
      row = candidate;
    }
  }
  return row;
};

/**
 * Лига конкретного подменного бота по РЕЙТИНГУ самого слабого живого игрока
 * лобби (см. VISIBLE_RANKED_BOT_LEAGUE_DISTRIBUTION). Таблица общая и для
 * обычного лобби, и для дуэли: дуэль влияет только на то, что подмена там
 * обязательна (см. resolveVisibleRankedBots), а не на силу соперника.
 */
export const resolveVisibleBotLeague = ({weakestRating, roll = Math.random}) => {
  const {leagues} = getVisibleRankedBotLeagueRow(weakestRating);
  const value = roll();
  let passed = 0;
  for (const [leagueId, chance] of leagues) {
    passed += chance;
    if (value < passed) {
      return leagueId;
    }
  }
  // Ошибка округления долей в конце шкалы — последняя лига строки
  return leagues[leagues.length - 1][0];
};

/**
 * Правило подмены для лобби по рейтингам ЖИВЫХ игроков: с какой вероятностью
 * менять бота (по ЛИГЕ самого слабого) и от какого рейтинга разыгрывать лигу
 * самого бота (weakestRating — см. resolveVisibleBotLeague).
 * null — считать не от кого (пустое лобби).
 */
export const resolveVisibleRankedBotRule = (humanRatings) => {
  if (!humanRatings || humanRatings.length === 0) {
    return null;
  }

  // Лиги монотонны по рейтингу, поэтому минимальный рейтинг и есть самая
  // слабая лига лобби
  const weakestRating = Math.min(...humanRatings.map((rating) => rating || 0));
  const weakestLeagueId = getLeagueByRating(weakestRating).id;

  const rule = VISIBLE_RANKED_BOT_LEAGUE_RULES[weakestLeagueId];

  return rule
    ? {leagueId: weakestLeagueId, ...rule, weakestRating}
    : null;
};

/** Уровень бонуса по лиге САМОГО бота */
export const getVisibleRankedBotBonusType = (leagueId) => (
  (VISIBLE_RANKED_BOT_LEAGUE_RULES[leagueId] || {}).bonusType || PLAYER_BONUS_TYPES.NONE
);

/**
 * Витринные рейтинговые матчи ботов друг с другом (createRankBotLobby на
 * сервере): живых игроков в них нет, и правило выше считать не от кого —
 * поэтому шансы подмены заданы напрямую, на каждого бота отдельно.
 *
 * Зачем подменять вообще: при низком онлайне очередь добирает рейтинговые
 * лобби живых игроков ботами, и явные среди них попадаются часто. На этом фоне
 * матчи, где значка бота нет ни у кого и никогда, выглядят подозрительно.
 *
 * Пары [лига явного бота, шанс]; остаток до единицы — бот остается неявным.
 * Лиги только нижние: такие явные боты чаще всего встречаются и самим игрокам.
 */
export const BOTS_ONLY_MATCH_VISIBLE_BOT_LEAGUE_CHANCES = [
  [LEAGUES.BRONZE, 0.3],
  [LEAGUES.SILVER, 0.2],
];

/**
 * Лига явного бота для витринного матча по одному броску
 * (см. BOTS_ONLY_MATCH_VISIBLE_BOT_LEAGUE_CHANCES); null — бот остается неявным.
 */
export const resolveBotsOnlyMatchVisibleBotLeague = (roll = Math.random) => {
  const value = roll();
  let passed = 0;
  for (const [leagueId, chance] of BOTS_ONLY_MATCH_VISIBLE_BOT_LEAGUE_CHANCES) {
    passed += chance;
    if (value < passed) {
      return leagueId;
    }
  }
  return null;
};

/**
 * С этой лиги явный бот всегда выходит на матч в фракционных образах
 * отрядов (если они у его фракции есть): сильный соперник и выглядит
 * «прокачанным». Явные боты ниже и неявные боты получают образы отрядов
 * случайным броском (см. pickBotHasUnitSkin на сервере)
 */
export const VISIBLE_RANKED_BOT_UNIT_SKIN_MIN_LEAGUE = LEAGUES.SILVER;

/**
 * @param {string} [leagueId] - лига САМОГО явного бота
 * @returns {boolean} всегда ли он в образах отрядов
 */
export const checkIfVisibleBotAlwaysHasUnitSkin = (leagueId) => {
  const league = getLeagueConfig(leagueId);
  const minLeague = getLeagueConfig(VISIBLE_RANKED_BOT_UNIT_SKIN_MIN_LEAGUE);
  return Boolean(league && minLeague && league.minRating >= minLeague.minRating);
};
